import type { GridColDef } from '@mui/x-data-grid';
import type { Theme, SxProps } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useParams } from 'react-router';

import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { paperClasses } from '@mui/material/Paper';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import { SX_GRADE_CANVAS } from 'src/auditoria/components/ds';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { formatDateBr, formatCurrency, getSituacaoLabel, getSituacaoColor } from '../Nfse/types';
import { type ServicoTomadoDetail, type ServicoTomadoDetailResponse } from './types';

// ----------------------------------------------------------------------
// Serviços Tomados → Visualizar. Read-only: o contribuinte é o TOMADOR, não pode
// cancelar/corrigir a nota (isso é do emissor). Exibe dados da nota + bloco do
// emissor/prestador + itens.

// ----------------------------------------------------------------------
// Casulo do canvas SEM o respiro lateral.
//
// A grade desta tela mora dentro de um `<Card sx={{ p: 3 }}>`, que já paga 24px de cada
// lado; com o
// recuo de 16px do kit as colunas ficariam 40px para dentro e sairiam do prumo do
// título do cartão. Por isso o `paddingInline` do `.MuiDataGrid-main` é zerado — e SÓ
// ele: o `::before` (faixa de fundo do cabeçalho) e o `::after` (as duas réguas do
// `.thead`) continuam, porque o kit desliga as bordas nativas em
// `.MuiDataGrid-columnHeaders` e sem os pseudo-elementos o cabeçalho ficaria sem régua
// nenhuma. As barras de rolagem voltam para a borda: são `position:absolute` na caixa de
// padding do `main` e ficariam 16px soltas.
//
// O `sx` do kit chega tipado com o `Theme` do app e o `TxDataGrid` do portal redeclara a
// prop como `SxProps` — a conversão é só de tipo, o objeto é o mesmo (mesma nota do
// `SubUsuarios/List`).
// ----------------------------------------------------------------------

const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_CANVAS = SX_GRADE as Record<string, SystemStyleObject<Theme>>;

const SX_GRADE_SEM_RESPIRO = {
  ...SX_CANVAS,
  '& .MuiDataGrid-main': { ...SX_CANVAS['& .MuiDataGrid-main'], paddingInline: 0 },
  '& .MuiDataGrid-scrollbar--vertical': { right: 0 },
  '& .MuiDataGrid-scrollbar--horizontal': { left: 0, width: '100%' },
} as SxProps;
/**
 * O `TxDataGrid` se embrulha num `<Paper variant="outlined">` próprio
 * (`TxDataGridClient.tsx:136`). Dentro do cartão da tela isso desenha uma segunda
 * superfície sobre a primeira — um cartão dentro do cartão — e, como a grade está sem o
 * respiro de 16px, as colunas encostam naquela borda interna em vez de ficarem no prumo
 * do título. Achatado, o cartão da tela volta a ser a única superfície e o eixo é um só.
 * Mesmo bloco de `conta-fiscal/views/index-view.tsx:133` e do `GradeCanvas`.
 */
const SX_ACHATA_PAPER_DA_GRADE = {
  [`& > .${paperClasses.root}`]: {
    border: 0,
    borderRadius: 0,
    boxShadow: 'none',
    backgroundImage: 'none',
  },
} as const;

function CampoInfo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Grid>
  );
}

export default function ServicosTomadosDetailPage() {
  const { id = '' } = useParams();
  const key = `/portal-api/servicos-tomados/${id}`;
  const { data, isLoading } = useSWR<ServicoTomadoDetailResponse>(id ? key : null, fetcher);
  const nota: ServicoTomadoDetail | undefined = data?.nota;

  // Download autenticado do XML (o Bearer não vai num <a href> puro) — mesma
  // função do sistema (nfse.montar_xml202_id), rota tomador-scoped.
  const baixarXml = async () => {
    try {
      const res = await axiosInstance.get(`/portal-api/servicos-tomados/${id}/xml`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `nfse-${id}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Falha ao baixar o XML.');
    }
  };

  type ItemRow = ServicoTomadoDetail['itens'][number] & { id: number };
  const itensColumns: GridColDef<ItemRow>[] = [
    { field: 'ordem', headerName: '#', width: 60, sortable: false, valueGetter: (_v, row) => row.id + 1 },
    { field: 'descricao', headerName: 'Descrição', flex: 1.6, minWidth: 220 },
    { field: 'quantidade', headerName: 'Qtde', flex: 0.5, minWidth: 90, align: 'right', headerAlign: 'right' },
    {
      field: 'valor_unitario',
      headerName: 'Vl. Unitário',
      flex: 0.8,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => formatCurrency(row.valor_unitario),
    },
    {
      field: 'valor_total',
      headerName: 'Vl. Total',
      flex: 0.8,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => formatCurrency(row.valor_total),
    },
  ];
  const itensRows = (nota?.itens ?? []).map((it, i) => ({ id: i, ...it }));

  const e = nota?.emissor;
  const enderecoLinha = e ? [e.logradouro, e.numero].filter(Boolean).join(', ') : '';
  const complementoLinha = e ? [e.bairro, e.cep, e.uf].filter(Boolean).join(' · ') : '';

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={nota ? `Nota ${nota.numero ?? ''}` : 'Serviço Tomado'}
        links={[
          { name: 'Serviços Tomados', href: '/servicos-tomados' },
          { name: 'Consultar', href: '/servicos-tomados' },
          { name: 'Detalhe' },
        ]}
        action={
          nota && (
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:export-bold" />}
              onClick={baixarXml}
            >
              Baixar XML
            </Button>
          )
        }
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      {!nota ? (
        <Card sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isLoading ? 'Carregando...' : 'Nota não encontrada.'}
          </Typography>
        </Card>
      ) : (
        <Stack spacing={3}>
          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Dados da nota
            </Typography>
            <Grid container spacing={2.5}>
              <CampoInfo label="Número" value={nota.numero} />
              <CampoInfo label="Série" value={nota.serie} />
              <CampoInfo
                label="Situação"
                value={
                  <Label color={getSituacaoColor(nota.situacao)}>
                    {getSituacaoLabel(nota.situacao)}
                  </Label>
                }
              />
              <CampoInfo label="Emissão" value={formatDateBr(nota.data_emissao)} />
              <CampoInfo label="Competência" value={nota.competencia} />
              <CampoInfo label="Autenticação" value={nota.autenticacao} />
              <CampoInfo label="Valor da nota" value={formatCurrency(nota.valor)} />
              <CampoInfo label="Valor dos serviços" value={formatCurrency(nota.valor_servicos)} />
              <CampoInfo
                label="Alíquota"
                value={nota.aliquota !== null && nota.aliquota !== undefined ? `${nota.aliquota}%` : null}
              />
              <CampoInfo label="ISS" value={formatCurrency(nota.iss_valor)} />
              <CampoInfo label="IR" value={formatCurrency(nota.ir_valor)} />
              <CampoInfo label="CSLL" value={formatCurrency(nota.csll_valor)} />
              <CampoInfo label="PIS" value={formatCurrency(nota.pis_valor)} />
              <CampoInfo label="COFINS" value={formatCurrency(nota.cofins_valor)} />
              <CampoInfo label="INSS" value={formatCurrency(nota.inss_valor)} />
              <CampoInfo label="Outras retenções" value={formatCurrency(nota.outras_retencoes)} />
            </Grid>
            {nota.descricao && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Discriminação
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {nota.descricao}
                </Typography>
              </>
            )}
          </Card>

          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Emissor / Prestador
            </Typography>
            {e ? (
              <Grid container spacing={2.5}>
                <CampoInfo label="Nome / Razão social" value={e.nome} />
                <CampoInfo label="Nome fantasia" value={e.fantasia} />
                <CampoInfo label="CPF / CNPJ" value={e.cnpj} />
                <CampoInfo label="Endereço" value={enderecoLinha} />
                <CampoInfo label="Bairro / CEP / UF" value={complementoLinha} />
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sem emissor informado.
              </Typography>
            )}
          </Card>

          <Card sx={{ p: 3, ...SX_ACHATA_PAPER_DA_GRADE }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Itens
            </Typography>
            <TxDataGrid<ItemRow>
              rows={itensRows}
              columns={itensColumns}
              initialPageSize={5}
              pageSizeOptions={[5, 10]}
              {...PROPS_GRADE}
              sx={SX_GRADE_SEM_RESPIRO}
            />
          </Card>
        </Stack>
      )}
    </DashboardContent>
  );
}
